import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import {useCallback, useMemo, useState} from 'react';
import {categoryGroupsApi} from '../api';
import {
  CategoryGroupsFilter,
  CategoryGroupsWithMetaModel,
  CategoryGroupModel,
  CreateCategoryGroupDTO,
  EditCategoryGroupParams,
} from '../model';
import {useNotifications} from '../../../shared';

type CategoryGroupsState = {
  getInfiniteCategoryGroups: UseInfiniteQueryResult<InfiniteData<CategoryGroupsWithMetaModel>, Error>;
  filters: CategoryGroupsFilter;
  updateFilter: (newFilter: Partial<CategoryGroupsFilter>) => void;
  createCategoryGroupMutation: UseMutationResult<CategoryGroupModel, Error, CreateCategoryGroupDTO>;
  updateCategoryGroupMutation: UseMutationResult<CategoryGroupModel, Error, EditCategoryGroupParams>;
  deleteCategoryGroupMutation: UseMutationResult<void, Error, string>;
};

const DEFAULT_PAGE_SIZE = 25;

export const useCategoryGroups = (initialFilter: CategoryGroupsFilter = {}): CategoryGroupsState => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<CategoryGroupsFilter>(() => ({
    pageSize: DEFAULT_PAGE_SIZE,
    ...initialFilter,
  }));
  const {createNotification} = useNotifications();

  const infiniteQueryFilters = useMemo(() => {
    const {after, ...rest} = filters as any;
    return rest;
  }, [filters]);

  const getInfiniteCategoryGroups = useInfiniteQuery<
    CategoryGroupsWithMetaModel,
    Error,
    InfiniteData<CategoryGroupsWithMetaModel>,
    ['categoryGroups', 'infinite', Omit<CategoryGroupsFilter, 'after'>],
    string | undefined
  >({
    queryKey: ['categoryGroups', 'infinite', infiniteQueryFilters],
    queryFn: ({pageParam}) => {
      return categoryGroupsApi.getCategoryGroups({
        ...infiniteQueryFilters,
        pageSize: filters.pageSize,
        after: pageParam,
      });
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage) => {
      return lastPage.pageInfo.hasNextPage && lastPage.pageInfo.endCursor
        ? lastPage.pageInfo.endCursor
        : undefined;
    },
    staleTime: 60 * 1000,
  });

  const updateFilter = useCallback((newFilter: Partial<CategoryGroupsFilter>) => {
    setFilters((prev) => ({
      ...prev,
      ...newFilter,
    }));
  }, []);

  const createCategoryGroupMutation = useMutation({
    mutationFn: categoryGroupsApi.createCategoryGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['categoryGroups', 'infinite'], refetchType: 'all'});
      createNotification({
        title: 'Группа категорий успешно создана',
        name: 'createCategoryGroup',
        theme: 'success',
      });
    },
  });

  const updateCategoryGroupMutation = useMutation({
    mutationFn: categoryGroupsApi.updateCategoryGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['categoryGroups', 'infinite'], refetchType: 'all'});
      queryClient.invalidateQueries({queryKey: ['categoryGroups']});
      createNotification({
        title: 'Группа категорий успешно обновлена',
        name: 'updateCategoryGroup',
        theme: 'success',
      });
    },
  });

  const deleteCategoryGroupMutation = useMutation({
    mutationFn: categoryGroupsApi.deleteCategoryGroup,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['categoryGroups', 'infinite'], refetchType: 'all'});
      queryClient.invalidateQueries({queryKey: ['categoryGroups']});
      createNotification({
        title: 'Группа категорий успешно удалена',
        name: 'deleteCategoryGroup',
        theme: 'success',
      });
    },
  });

  return {
    getInfiniteCategoryGroups,
    filters,
    updateFilter,
    createCategoryGroupMutation,
    updateCategoryGroupMutation,
    deleteCategoryGroupMutation,
  };
};
